import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { tenderOf } from '../services/payments';
import { formatDate } from '../services/datetime';
import { printReportAuto } from '../services/receipt';
import './Sections.css';
import './Reports.css';

// Customer Report dashboard (customer_report_dashboard_layout.xml) + Customer
// Ledger (customer_ledger_layout.xml). Report types: All Customer List,
// Credit Sale, Item Wise Report, Ledger, Bill Summary. Ledger / Item-Wise need
// a specific customer; the others run across all (or the selected) customer.

type ReportKey = 'all_customers' | 'credit_sale' | 'item_wise' | 'ledger' | 'bill_summary';

const CARDS: { key: ReportKey; title: string; desc: string; needsCustomer?: boolean }[] = [
  { key: 'all_customers', title: 'All Customer List', desc: 'Every customer with balance, advance & points' },
  { key: 'credit_sale', title: 'Credit Sale', desc: 'Bills (partly) settled on credit & outstanding' },
  { key: 'item_wise', title: 'Item Wise Report', desc: 'Items bought by a customer', needsCustomer: true },
  { key: 'ledger', title: 'Ledger', desc: 'Debit / credit-received with running balance', needsCustomer: true },
  { key: 'bill_summary', title: 'Bill Summary', desc: 'Per-customer bill count, total & average' },
];

interface Result {
  title: string;
  columns: string[];
  rows: (string | number)[][];
  numericCols?: number[];
  footer?: string;
}

const CustomerReport: React.FC = () => {
  const { sales, customers, cashflow, formatMoney } = usePos();
  const [active, setActive] = useState<ReportKey | null>(null);
  const [customerId, setCustomerId] = useState<string>('');

  const completed = useMemo(
    () => sales.filter((s) => s.status === 'completed' && s.total > 0),
    [sales]
  );

  const card = CARDS.find((c) => c.key === active);
  const selected = customers.find((c) => c.id === customerId);

  const result: Result | null = useMemo(() => {
    if (!active) return null;
    const m = (n: number) => formatMoney(n);

    switch (active) {
      case 'all_customers':
        return {
          title: 'All Customer List',
          columns: ['Name', 'Mobile', 'Old Balance', 'Balance Due', 'Advance', 'Points'],
          rows: customers.map((c) => [
            c.name,
            c.phone ?? '—',
            m(c.oldBalance ?? 0),
            m(c.creditBalance ?? 0),
            m(c.advance ?? 0),
            c.loyaltyPoints ?? 0,
          ]),
        };

      case 'credit_sale': {
        const rows = completed
          .map((s) => ({ s, credit: tenderOf(s, 'credit') }))
          .filter((x) => x.credit > 0)
          .filter((x) => !customerId || x.s.customerId === customerId);
        const total = rows.reduce((a, x) => a + x.credit, 0);
        return {
          title: 'Credit Sale',
          columns: ['Date', 'Bill No', 'Customer', 'Bill Total', 'On Credit'],
          rows: rows
            .sort((a, b) => +new Date(b.s.timestamp) - +new Date(a.s.timestamp))
            .map((x) => [formatDate(x.s.timestamp), x.s.billNo, x.s.customerName ?? 'Walk-in', m(x.s.total), m(x.credit)]),
          numericCols: [4],
          footer: `Total on credit: ${m(total)}`,
        };
      }

      case 'item_wise': {
        if (!selected) return { title: 'Item Wise Report', columns: ['Info'], rows: [['Select a customer above.']] };
        const map = new Map<string, { qty: number; amt: number }>();
        completed
          .filter((s) => s.customerId === customerId)
          .forEach((s) => s.items.forEach((i) => {
            const r = map.get(i.name) ?? { qty: 0, amt: 0 };
            r.qty += i.quantity; r.amt += i.total; map.set(i.name, r);
          }));
        return {
          title: `Item Wise — ${selected.name}`,
          columns: ['Product', 'Qty', 'Amount'],
          rows: Array.from(map.entries())
            .sort((a, b) => b[1].amt - a[1].amt)
            .map(([name, v]) => [name, v.qty, m(v.amt)]),
          numericCols: [2],
        };
      }

      case 'ledger': {
        if (!selected) return { title: 'Ledger', columns: ['Info'], rows: [['Select a customer above.']] };
        const opening = selected.oldBalance ?? 0;
        type Entry = { ts: string; particulars: string; type: string; debit: number; credit: number };
        const entries: Entry[] = [];
        // Debits: the credit-tendered portion of each bill.
        completed
          .filter((s) => s.customerId === customerId)
          .forEach((s) => {
            const credit = tenderOf(s, 'credit');
            if (credit > 0) {
              entries.push({
                ts: s.timestamp,
                particulars: `Bill ${s.billNo}`,
                type: (s.orderType ?? 'sale').toUpperCase(),
                debit: credit,
                credit: 0,
              });
            }
          });
        // Credits received + advances reduce the balance.
        cashflow
          .filter((c) => c.customerId === customerId && (c.type === 'credit_received' || c.type === 'advance'))
          .forEach((c) =>
            entries.push({
              ts: c.timestamp,
              particulars: c.note || (c.type === 'advance' ? 'Advance' : 'Payment'),
              type: c.type === 'advance' ? 'ADVANCE' : 'RECEIPT',
              debit: 0,
              credit: c.amount,
            })
          );
        entries.sort((a, b) => +new Date(a.ts) - +new Date(b.ts));

        let bal = opening;
        const rows: (string | number)[][] = [];
        rows.push(['—', 'Opening Balance', 'OPENING', '', '', m(opening)]);
        entries.forEach((e) => {
          bal += e.debit - e.credit;
          rows.push([
            formatDate(e.ts),
            e.particulars,
            e.type,
            e.debit ? m(e.debit) : '',
            e.credit ? m(e.credit) : '',
            m(bal),
          ]);
        });
        return {
          title: `Ledger — ${selected.name}`,
          columns: ['Date', 'Particulars', 'Invoice Type', 'Debit', 'Credit Recd', 'Balance'],
          footer: `${selected.name} — Closing balance: ${m(bal)}`,
          rows,
        };
      }

      case 'bill_summary': {
        const map = new Map<string, { name: string; bills: number; total: number }>();
        completed
          .filter((s) => !customerId || s.customerId === customerId)
          .forEach((s) => {
            const key = s.customerId ?? 'walkin';
            const r = map.get(key) ?? { name: s.customerName ?? 'Walk-in', bills: 0, total: 0 };
            r.bills += 1; r.total += s.total; map.set(key, r);
          });
        return {
          title: 'Bill Summary',
          columns: ['Customer', 'Bills', 'Total', 'Average'],
          rows: Array.from(map.values())
            .sort((a, b) => b.total - a.total)
            .map((v) => [v.name, v.bills, m(v.total), m(v.total / v.bills)]),
          numericCols: [1, 2, 3],
        };
      }

      default:
        return null;
    }
  }, [active, customerId, selected, completed, customers, cashflow, formatMoney]);

  const exportCsv = () => {
    if (!result) return;
    const csv = [result.columns, ...result.rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.title.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printUsb = () => {
    if (!result) return;
    // Carry the on-screen footer (totals / closing balance) onto the slip.
    const rows = result.footer ? [...result.rows, [result.footer]] : result.rows;
    printReportAuto(result.title, selected ? selected.name : 'All Customers', result.columns, rows);
  };

  return (
    <div className="section-page rp-page">
      <div className="page-header">
        <h1>CUSTOMER REPORT</h1>
        <div className="rp-range">
          <label>
            Customer
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">All Customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="rp-section">
        <div className="rp-cards">
          {CARDS.map((c) => (
            <button
              key={c.key}
              className={`rp-card ${active === c.key ? 'active' : ''}`}
              onClick={() => setActive(c.key)}
            >
              <span className="rp-card-title">{c.title}</span>
              <span className="rp-card-desc">
                {c.desc}
                {c.needsCustomer && !customerId ? ' · pick a customer' : ''}
              </span>
            </button>
          ))}
        </div>
      </div>

      {result && (
        <div className="section-card" style={{ marginTop: 12 }}>
          <div className="rp-result-head">
            <h2>{result.title}</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-grey" onClick={() => window.print()}>Print</button>
              <button className="btn-grey" onClick={printUsb}>🖨 USB Print</button>
              <button className="btn-grey" onClick={exportCsv}>CSV</button>
              <button className="btn-teal" onClick={() => setActive(null)}>Close</button>
            </div>
          </div>
          <div className="rp-result-scroll">
            <table className="section-table">
              <thead>
                <tr>
                  {result.columns.map((c, i) => (
                    <th key={i} className={result.numericCols?.includes(i) || i === result.columns.length - 1 ? 'right' : ''}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 ? (
                  <tr><td colSpan={result.columns.length} className="muted">No data.</td></tr>
                ) : (
                  result.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci} className={result.numericCols?.includes(ci) || ci === row.length - 1 ? 'right' : ''}>{cell}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {result.footer && <div className="cr-footer">{result.footer}</div>}
        </div>
      )}

      {!result && <p className="muted" style={{ padding: 16 }}>Pick a report above. Ledger and Item-Wise need a customer selected.</p>}
    </div>
  );
};

export default CustomerReport;
